import React from "react";
import {
  Pencil,
  Trash,
  Eye,
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCachedDoctorsSearch } from "../../hooks/useCachedDoctorsSearch";
import { API_ENDPOINTS } from "../../services/api/apiConfig";
import axiosInstance from "../../services/axiosInstance";
import Header from "../common/Header";
import LoadingScreen from "../common/LoadingScreen";
import type { Doctor } from "../../types";
import { useConfirm } from "../../context/ConfirmContext";
import { toast } from 'react-hot-toast';
import { useAdminDoctorList } from "../../hooks/useAdminDoctorList";

const DoctorAccount: React.FC = () => {
  const {
    doctors,
    loading,
    totalPages,
    filters,
    setFilters,
  } = useAdminDoctorList();

  const navigate = useNavigate();

  // Utiliser le hook de cache pour la recherche de médecins
  const { refetch } = useCachedDoctorsSearch();
  const { confirm } = useConfirm();

  const handleViewDoctor = async (doctorId: number) => {
    try {
      await axiosInstance.get(API_ENDPOINTS.USERS.DOCTORS.GET_BY_ID(doctorId));
      navigate(`/admin/dashboard/account/doctor/view/${doctorId}`);
    } catch (err) {
      console.error("Error navigating to doctor view:", err);
    }
  };

  const handleDeleteDoctor = async (doctorId: number) => {
    const isConfirmed = await confirm("Êtes-vous sûr de vouloir supprimer ce docteur ?", {
      title: "Supprimer le docteur",
      confirmText: "Supprimer",
      cancelText: "Annuler"
    });

    if (!isConfirmed) return;
    try {
      await axiosInstance.delete(API_ENDPOINTS.ADMINS.DELETE(doctorId));
      refetch(""); // Refresh list after delete
      toast.success("Docteur supprimé avec succès");
    } catch (err: any) {
      console.error("Error deleting doctor:", err);
      const errorMessage = err.response?.data?.error || "Erreur lors de la suppression du docteur";
      toast.error(errorMessage);
    }
  };

  const handleUpdateDoctor = async (doctorId: number) => {
    try {
      await axiosInstance.get(API_ENDPOINTS.USERS.DOCTORS.GET_BY_ID(doctorId));
      navigate(`/admin/dashboard/account/doctor/edit/${doctorId}`);
    } catch (err) {
      console.error("Error navigating to doctor update:", err);
    }
  };

  const formatDate = (dateString: string) =>
    dateString ? new Date(dateString).toLocaleDateString("fr-FR") : "";

  const paginate = (pageNumber: number) => setFilters({ currentPage: pageNumber });
  const nextPage = () => setFilters({ currentPage: Math.min(filters.currentPage + 1, totalPages) });
  const prevPage = () => setFilters({ currentPage: Math.max(filters.currentPage - 1, 1) });
  const firstPage = () => setFilters({ currentPage: 1 });
  const lastPage = () => setFilters({ currentPage: totalPages });


  if (loading && doctors.length === 0)
    return <LoadingScreen message="Chargement des docteurs..." role="admin" />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <Header
        title="Gestion des Docteurs"
        description="Visualisez et gérez les comptes des docteurs."
        action={{
          label: "Nouveau Docteur",
          to: "/admin/dashboard/account/doctor/new",
          icon: <Plus className="w-4 h-4" />,
        }}
        role="admin"
      />
      <div className="px-6">
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 max-w-6xl mx-auto relative min-h-[400px]">
          {loading && doctors.length > 0 && (
            <div className="absolute inset-0 bg-white/50 z-10 flex items-center justify-center rounded-2xl">
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-100 border-t-blue-600 mb-3"></div>
                <p className="text-sm text-blue-600 font-medium">
                  Actualisation...
                </p>
              </div>
            </div>
          )}
          <div className="relative flex-grow mb-6">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Rechercher par nom, spécialité, téléphone ou email..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-colors"
              value={filters.searchTerm}
              onChange={(e) => {
                setFilters({ searchTerm: e.target.value });
              }}
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full table-auto divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  {[
                    { label: "Nom", class: "md:w-1/6" },
                    { label: "Prénom", class: "md:w-1/6" },
                    {
                      label: "Spécialité",
                      class: "hidden lg:table-cell md:w-1/6",
                    },
                    {
                      label: "Date de naissance",
                      class: "hidden lg:table-cell md:w-1/12",
                    },
                    { label: "Genre", class: "hidden lg:table-cell md:w-1/12" },
                    { label: "Téléphone", class: "md:w-1/6" },
                    { label: "Email", class: "md:w-1/6" },
                    {
                      label: "Adresse",
                      class: "hidden lg:table-cell md:w-1/6",
                    },
                    { label: "Actions", class: "md:w-1/12" },
                  ].map((header, index) => (
                    <th
                      key={index}
                      className={`px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${header.class}`}
                    >
                      {header.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {doctors.length > 0 ? (
                  doctors.map((doctor: Doctor, index: number) => (
                    <tr
                      key={doctor.id}
                      className={`hover:bg-gray-50 transition-colors animate-slideInUp delay-${index * 100
                        }`}
                    >
                      <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900 text-sm">
                        {doctor.lastName}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-sm">
                        {doctor.firstName}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-sm hidden lg:table-cell truncate max-w-[150px]">
                        {doctor.specialty}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-sm hidden lg:table-cell">
                        {formatDate(doctor.dateOfBirth)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-sm hidden lg:table-cell">
                        {doctor.gender === "M"
                          ? "Masculin"
                          : doctor.gender === "F"
                            ? "Féminin"
                            : "Autre"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-sm">
                        {doctor.phone}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-sm truncate max-w-[150px]">
                        {doctor.email}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-sm hidden lg:table-cell truncate max-w-[150px]">
                        {doctor.address}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <div className="flex justify-center space-x-2">
                          <button
                            className="p-1.5 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-800 transition-colors"
                            onClick={() => handleViewDoctor(doctor.id)}
                            aria-label="Voir le docteur"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            className="p-1.5 rounded-full bg-yellow-50 text-yellow-600 hover:bg-yellow-100 hover:text-yellow-800 transition-colors"
                            onClick={() => handleUpdateDoctor(doctor.id)}
                            aria-label="Modifier le docteur"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            className="p-1.5 rounded-full bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-800 transition-colors"
                            onClick={() => handleDeleteDoctor(doctor.id)}
                            aria-label="Supprimer le docteur"
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-4 py-3 text-center text-gray-500 text-sm"
                    >
                      Aucun docteur trouvé
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center">
            <div className="text-sm text-gray-600 mb-4 md:mb-0">
              Affichage de la page {filters.currentPage} sur {totalPages}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={firstPage}
                disabled={filters.currentPage === 1}
                className={`p-1.5 rounded-md ${filters.currentPage === 1
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
                aria-label="Première page"
              >
                <ChevronsLeft size={16} />
              </button>
              <button
                onClick={prevPage}
                disabled={filters.currentPage === 1}
                className={`p-1.5 rounded-md ${filters.currentPage === 1
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
                aria-label="Page précédente"
              >
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum =
                  totalPages <= 5
                    ? i + 1
                    : filters.currentPage <= 3
                      ? i + 1
                      : filters.currentPage >= totalPages - 2
                        ? totalPages - 4 + i
                        : filters.currentPage - 2 + i;
                return (
                  <button
                    key={pageNum}
                    onClick={() => paginate(pageNum)}
                    className={`w-8 h-8 rounded-md text-sm ${filters.currentPage === pageNum
                      ? "bg-blue-600 text-white"
                      : "text-gray-700 hover:bg-gray-100"
                      }`}
                    aria-label={`Page ${pageNum}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={nextPage}
                disabled={filters.currentPage === totalPages}
                className={`p-1.5 rounded-md ${filters.currentPage === totalPages
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
                aria-label="Page suivante"
              >
                <ChevronRight size={16} />
              </button>
              <button
                onClick={lastPage}
                disabled={filters.currentPage === totalPages}
                className={`p-1.5 rounded-md ${filters.currentPage === totalPages
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
                aria-label="Dernière page"
              >
                <ChevronsRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorAccount;
